/**
 * A tabela de três níveis dos cartões do Fechamento: categoria, subcategoria e
 * transação.
 *
 * Papel: reproduz `renderCatTable` (l. 2618–2686) para "Saídas por Categoria",
 * "Entradas por Categoria" e "Movimentação Bancária": a linha de categoria com
 * a bolinha, o nome, a barra de participação, o valor e o percentual; as
 * linhas de subcategoria sempre à vista, por valor decrescente, com o
 * percentual sobre a categoria; e as transações de cada subcategoria,
 * escondidas até o clique.
 *
 * Como usar: as categorias chegam agregadas e ordenadas pela API
 * (`TabelaDoFechamento`), e a tabela só desenha. `corDoValor` pinta os valores
 * de Entradas (`sucesso`) e de Movimentação (`movimentacao`); sem ela, o valor
 * da categoria sai na cor da categoria e o da transação em `--perigo`. Abrir
 * uma subcategoria é estado da própria tabela, e quem monta passa
 * `key={competencia}` para trocar de mês fechar todas, como na origem.
 *
 * A bolinha e a barra pintam com a cor da categoria, que é dado gravado no
 * plano de contas e por isso entra por `style`; a categoria sem cor sai no
 * cinza de reserva `--emb-cinza-texto`, o `#6b7280` da origem. A subcategoria
 * inteira é clicável pelo botão do nome, que cobre a linha por um `::after`
 * — é o que dá à linha o clique da origem sem tirar dela a semântica de linha
 * de tabela.
 *
 * No papel do Fechamento (marca `imprimindo-fechamento`), as transações e as
 * setas somem e o texto da tabela vai a 11 px (l. 284–292).
 */
import {
  ROTULOS_FORMA,
  type CategoriaDoFechamento,
  type TabelaDoFechamento,
  type TransacaoDoFechamento,
} from '@concierge/shared';
import { IconClock, IconSend } from '@tabler/icons-react';
import { useState } from 'react';

import { formatarData } from '@/shared/lib/formato';
import { formatarPercentual, formatarReais } from '../formato';
import { BarraDeProgresso } from './kit/barra-de-progresso';
import { Selo } from './kit/selo';
import { LinhaVazia } from './kit/tabela';

/** A cor dos valores que o cartão impõe: Entradas e Movimentação. */
export type CorDoValorDaTabela = 'sucesso' | 'movimentacao';

/** A variável CSS de cada cor imposta. */
const VARIAVEL_DA_COR: Readonly<Record<CorDoValorDaTabela, string>> = {
  sucesso: 'var(--sucesso)',
  movimentacao: 'var(--emb-movimentacao)',
};

/** A cor da categoria sem cor no plano. */
const COR_DE_RESERVA = 'var(--emb-cinza-texto)';

/** O que a tabela mostra enquanto não tem as categorias. */
export type SituacaoDaTabela = 'pronta' | 'carregando' | 'falhou';

interface TabelaPorCategoriaProps {
  /** A tabela do mês; nula enquanto o Fechamento carrega ou depois da falha. */
  tabela: TabelaDoFechamento | null;
  situacao: SituacaoDaTabela;
  onTentarDeNovo: () => void;
  corDoValor?: CorDoValorDaTabela;
}

/**
 * O selo da transação: ✓ no pago, relógio no provisionado, aviãozinho no
 * lançado.
 *
 * @param transacao - A transação.
 * @returns O selo de 9 px.
 */
function SeloDaTransacao({ transacao }: { transacao: TransacaoDoFechamento }) {
  if (transacao.status === 'pago') {
    return (
      <Selo cor="verde" className="text-[9px]">
        ✓
      </Selo>
    );
  }
  if (transacao.status === 'provisionado') {
    return (
      <Selo cor="amarelo" className="text-[9px]">
        <IconClock size={11} aria-label="Provisionado" />
      </Selo>
    );
  }
  return (
    <Selo cor="lancado" className="text-[9px]">
      <IconSend size={11} aria-label="Lançado" />
    </Selo>
  );
}

/**
 * As linhas de uma categoria: a da categoria, as das subcategorias e as das
 * transações abertas.
 *
 * @param categoria - A categoria agregada.
 * @param total - O total da tabela, para o percentual da categoria.
 * @param corDoValor - A cor imposta pelo cartão, quando há.
 * @param abertas - As subcategorias abertas, por chave.
 * @param onAlternar - Abre ou fecha uma subcategoria.
 * @returns As linhas.
 */
function LinhasDaCategoria({
  categoria,
  total,
  corDoValor,
  abertas,
  onAlternar,
}: {
  categoria: CategoriaDoFechamento;
  total: number;
  corDoValor: CorDoValorDaTabela | undefined;
  abertas: ReadonlySet<string>;
  onAlternar: (chave: string) => void;
}) {
  const cor = categoria.cor ?? COR_DE_RESERVA;
  const corDoValorDaCategoria = corDoValor === undefined ? cor : VARIAVEL_DA_COR[corDoValor];
  const corDaTransacao =
    corDoValor === undefined ? 'var(--perigo)' : VARIAVEL_DA_COR[corDoValor];

  return (
    <>
      <tr className="border-b border-emb-borda last:border-b-0">
        <td className="w-[46%] px-3 py-[9px]">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-[9px] w-[9px] shrink-0 rounded-full"
              style={{ background: cor }}
            />
            <span className="font-outfit text-[13px] font-light text-foreground">
              {categoria.nome}
            </span>
          </div>
          <BarraDeProgresso
            percentual={total > 0 ? (categoria.valor / total) * 100 : 0}
            cor={cor}
          />
        </td>
        <td
          className="px-1 py-[9px] text-right text-[12px] font-bold tabular-nums"
          style={{ color: corDoValorDaCategoria }}
        >
          {formatarReais(categoria.valor)}
        </td>
        <td className="w-9 px-3 py-[9px] text-right text-[11px] text-texto-fraco">
          {formatarPercentual(categoria.valor, total)}
        </td>
      </tr>
      {categoria.subcategorias.map((subcategoria) => {
        const chave = `${categoria.nome}\u0000${subcategoria.nome}`;
        const temTransacoes = subcategoria.lancamentos.length > 0;
        const aberta = abertas.has(chave);
        return (
          <LinhasDaSubcategoria
            key={chave}
            nome={subcategoria.nome}
            valor={subcategoria.valor}
            valorDaCategoria={categoria.valor}
            transacoes={subcategoria.lancamentos}
            temTransacoes={temTransacoes}
            aberta={aberta}
            corDaTransacao={corDaTransacao}
            onAlternar={() => onAlternar(chave)}
          />
        );
      })}
    </>
  );
}

/**
 * A linha de uma subcategoria e, aberta, as das transações dela.
 *
 * @param nome - O nome da subcategoria.
 * @param valor - O valor dela.
 * @param valorDaCategoria - O valor da categoria, para o percentual.
 * @param transacoes - As transações pagas, por data.
 * @param temTransacoes - Se há transação para abrir.
 * @param aberta - Se as transações estão à vista.
 * @param corDaTransacao - A cor do valor de cada transação.
 * @param onAlternar - Abre ou fecha.
 * @returns As linhas.
 */
function LinhasDaSubcategoria({
  nome,
  valor,
  valorDaCategoria,
  transacoes,
  temTransacoes,
  aberta,
  corDaTransacao,
  onAlternar,
}: {
  nome: string;
  valor: number;
  valorDaCategoria: number;
  transacoes: readonly TransacaoDoFechamento[];
  temTransacoes: boolean;
  aberta: boolean;
  corDaTransacao: string;
  onAlternar: () => void;
}) {
  return (
    <>
      <tr
        className={
          temTransacoes
            ? 'relative cursor-pointer border-b border-emb-borda last:border-b-0 [&>td]:bg-emb-linha-sub'
            : 'border-b border-emb-borda last:border-b-0 [&>td]:bg-emb-linha-sub'
        }
      >
        <td className="py-1.5 pr-3 pl-7 font-sans text-[12px] font-light text-emb-texto-tabela">
          {temTransacoes ? (
            <button
              type="button"
              aria-expanded={aberta}
              onClick={onAlternar}
              className="cursor-pointer border-0 bg-transparent p-0 text-left font-sans font-light after:absolute after:inset-0"
            >
              <span
                aria-hidden="true"
                className="mr-[5px] inline-block align-middle text-[9px] leading-none text-texto-fraco print:[body[data-impressao=imprimindo-fechamento]_&]:hidden"
              >
                {aberta ? '▼' : '▶'}
              </span>
              <span>{nome}</span>
            </button>
          ) : (
            <>
              <span aria-hidden="true" className="inline-block w-3.5" />
              <span>{nome}</span>
            </>
          )}
        </td>
        <td className="px-1 py-1.5 text-right text-[12px] text-emb-texto-tabela tabular-nums">
          {formatarReais(valor)}
        </td>
        <td className="px-3 py-1.5 text-right text-[11px] text-texto-fraco">
          {formatarPercentual(valor, valorDaCategoria)}
        </td>
      </tr>
      {aberta &&
        transacoes.map((transacao) => (
          <tr
            key={transacao.id}
            className="border-b border-emb-borda [&>td]:bg-emb-linha-transacao print:[body[data-impressao=imprimindo-fechamento]_&]:hidden"
          >
            <td className="border-b border-emb-borda py-1 pr-3 pl-11 text-[11px] text-emb-texto-tabela">
              <span className="mr-1.5 text-texto-fraco">{formatarData(transacao.data)}</span>
              {transacao.descricao}
              {transacao.formaPagamento !== null && (
                <span className="ml-1 text-texto-fraco">
                  · {ROTULOS_FORMA[transacao.formaPagamento]}
                </span>
              )}
            </td>
            <td
              className="border-b border-emb-borda p-1 text-right text-[11px] font-bold tabular-nums"
              style={{ color: corDaTransacao }}
            >
              {formatarReais(transacao.valor)}
            </td>
            <td className="border-b border-emb-borda px-3 py-1 text-right">
              <SeloDaTransacao transacao={transacao} />
            </td>
          </tr>
        ))}
    </>
  );
}

/**
 * Uma tabela por categoria do Fechamento.
 *
 * @param tabela - As categorias do mês, ou nula enquanto não chegam.
 * @param situacao - `carregando` e `falhou` ocupam a tabela com a linha da base.
 * @param onTentarDeNovo - O "Tentar de novo" da falha.
 * @param corDoValor - A cor imposta pelo cartão; ausente em Saídas.
 * @returns A tabela.
 */
export function TabelaPorCategoria({
  tabela,
  situacao,
  onTentarDeNovo,
  corDoValor,
}: TabelaPorCategoriaProps) {
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Abre a subcategoria fechada e fecha a aberta.
   *
   * @param chave - A categoria e a subcategoria.
   */
  function handleAlternar(chave: string) {
    setAbertas((anteriores) => {
      const proximas = new Set(anteriores);
      if (proximas.has(chave)) proximas.delete(chave);
      else proximas.add(chave);
      return proximas;
    });
  }

  const linhaVazia = (situacaoDaLinha: 'vazia' | 'carregando' | 'falhou') => (
    <LinhaVazia
      colunas={3}
      situacao={situacaoDaLinha}
      textoDoVazio="Sem dados no mês."
      onTentarDeNovo={onTentarDeNovo}
      className="p-3 text-[12px]"
    />
  );

  return (
    <table className="w-full border-collapse text-[12px] print:[body[data-impressao=imprimindo-fechamento]_&_span]:!text-[11px] print:[body[data-impressao=imprimindo-fechamento]_&_td]:!text-[11px]">
      <tbody>
        {situacao !== 'pronta' || tabela === null
          ? linhaVazia(situacao === 'falhou' ? 'falhou' : 'carregando')
          : tabela.categorias.length === 0
            ? linhaVazia('vazia')
            : tabela.categorias.map((categoria) => (
                <LinhasDaCategoria
                  key={categoria.nome}
                  categoria={categoria}
                  total={tabela.total}
                  corDoValor={corDoValor}
                  abertas={abertas}
                  onAlternar={handleAlternar}
                />
              ))}
      </tbody>
    </table>
  );
}
